import React, { useState, useEffect, useCallback } from 'react';
// Removed Credentials import - using backend auth now
import { CreateTodoRequest } from '../types/todo';
import { User } from '../types';
import { 
  fetchHistoricalRankings, 
  HistoricalRankingData, 
  getTrendIcon, 
  getTrendColor, 
  formatPositionChange 
} from '../services/historicalRankings';

interface HistoricalRankingsPanelProps {
  property: string;
  locationFilter: string;
  user: User;
  onAddTodo?: (todo: Omit<CreateTodoRequest, 'userEmail' | 'propertyUrl'>) => Promise<any>;
  trackedKeywords?: Array<{ query: string; id: number; propertyUrl?: string }>;
  onTrackKeyword?: (query: string) => Promise<void>;
  onUntrackKeyword?: (keywordId: number) => Promise<void>;
}

const HistoricalRankingsPanel: React.FC<HistoricalRankingsPanelProps> = ({
  property,
  locationFilter,
  user,
  onAddTodo,
  trackedKeywords = [],
  onTrackKeyword,
  onUntrackKeyword
}) => {
  const [data, setData] = useState<HistoricalRankingData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 7 });
  const [queryFilter, setQueryFilter] = useState('');
  const [selectedTrendFilter, setSelectedTrendFilter] = useState<'up' | 'down' | 'stable' | 'new' | null>(null);
  const [addingTodos, setAddingTodos] = useState<Set<string>>(new Set());
  const [trackingKeywords, setTrackingKeywords] = useState<Set<string>>(new Set());
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const fetchData = useCallback(async () => {
    if (!property) return;
    
    setIsLoading(true);
    setProgress({ completed: 0, total: 7 });
    
    try {
      const result = await fetchHistoricalRankings(
        property,
        locationFilter,
        (progressUpdate) => {
          setProgress({ completed: progressUpdate.completed, total: progressUpdate.total });
        }
      );
      setData(result);
    } catch (error) {
      console.error('Error fetching historical rankings:', error);
    } finally {
      setIsLoading(false);
    }
  }, [property, locationFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter data based on query filter and trend filter
  const filteredData = data.filter(item => {
    const matchesQuery = item.query && item.query.toLowerCase().includes(queryFilter.toLowerCase());
    const matchesTrend = !selectedTrendFilter || item.trend === selectedTrendFilter;
    return matchesQuery && matchesTrend;
  });

  // Helper function to check if a keyword is tracked for current property
  const isKeywordTracked = (query: string) => {
    return trackedKeywords.some(tk => tk.query === query && (tk.propertyUrl === property || !tk.propertyUrl));
  };

  // Helper function to get tracked keyword ID for current property
  const getTrackedKeywordId = (query: string) => {
    const tracked = trackedKeywords.find(tk => tk.query === query && (tk.propertyUrl === property || !tk.propertyUrl));
    return tracked ? tracked.id : null;
  };

  // Handle tracking a keyword
  const handleTrackKeyword = async (item: HistoricalRankingData) => {
    if (!onTrackKeyword) return;
    
    setTrackingKeywords(prev => new Set(prev).add(item.query));
    
    try {
      await onTrackKeyword(item.query);
    } catch (error) {
      console.error('Error tracking keyword:', error);
    } finally {
      setTrackingKeywords(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.query);
        return newSet;
      });
    }
  };

  // Handle untracking a keyword
  const handleUntrackKeyword = async (item: HistoricalRankingData) => {
    if (!onUntrackKeyword) return;
    
    const keywordId = getTrackedKeywordId(item.query);
    if (!keywordId) return;
    
    setTrackingKeywords(prev => new Set(prev).add(item.query));
    
    try {
      await onUntrackKeyword(keywordId);
    } catch (error) {
      console.error('Error untracking keyword:', error);
    } finally {
      setTrackingKeywords(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.query);
        return newSet;
      });
    }
  };


  // Handle trend filter clicks
  const handleTrendFilterClick = (trend: 'up' | 'down' | 'stable' | 'new') => {
    if (selectedTrendFilter === trend) {
      // If clicking the same filter, deselect it
      setSelectedTrendFilter(null);
    } else {
      // Select the new filter
      setSelectedTrendFilter(trend);
    }
  };

  const formatPosition = (position: number | null) => {
    if (position === null) return '-';
    if (position > 100) return '100+';
    return position.toFixed(1);
  };

  const getPositionStyle = (current: number | null, previous: number | null) => {
    if (!current || !previous) return '';
    if (current < previous) return 'bg-green-50 text-green-800';
    if (current > previous) return 'bg-red-50 text-red-800';
    return '';
  };

  const downloadCSV = () => {
    if (filteredData.length === 0) return;

    // Check if user is on free plan (admins bypass this restriction)
    if (user.plan === 'free' && !user.isAdmin) {
      setShowUpgradeModal(true);
      return;
    }

    // Create CSV headers
    const headers = [
      'Query',
      'Trend',
      'Current Position',
      '1 Month Ago',
      '2 Months Ago', 
      '3 Months Ago',
      '4 Months Ago',
      '5 Months Ago',
      '6 Months Ago',
      'Position Change'
    ];

    // Convert data to CSV rows
    const csvRows = [
      headers.join(','),
      ...filteredData.map(item => [
        `"${item.query.replace(/"/g, '""')}"`, // Escape quotes in query
        item.trend,
        formatPosition(item.positions.current),
        formatPosition(item.positions.month1),
        formatPosition(item.positions.month2),
        formatPosition(item.positions.month3),
        formatPosition(item.positions.month4),
        formatPosition(item.positions.month5),
        formatPosition(item.positions.month6),
        formatPositionChange(item.changeFromPrevious)
      ].join(','))
    ];

    // Create CSV content
    const csvContent = csvRows.join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `historical-rankings-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddTodo = async (item: HistoricalRankingData) => {
    if (!onAddTodo) return;
    
    setAddingTodos(prev => new Set(prev).add(item.query));
    
    try {
      const priority = item.trend === 'up' ? 'medium' as const : 
                      item.trend === 'down' ? 'high' as const : 
                      'low' as const;
      
      const trendEmoji = getTrendIcon(item.trend);
      const currentPos = item.positions.current ? formatPosition(item.positions.current) : 'New';
      
      await onAddTodo({
        title: `Optimize ranking for: "${item.query}"`,
        description: `${trendEmoji} Historical ranking trend: ${item.trend}. Current position: ${currentPos}. Track and improve search performance for this query.`,
        priority: priority,
        sourcePanel: 'Historical Rankings'
      });
    } catch (error) {
      console.error('Error adding todo:', error);
    } finally {
      setAddingTodos(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.query);
        return newSet;
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            📈 Historical Rankings
          </h2>
          <p className="text-sm text-gray-600">Track position changes over 6 months</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={downloadCSV}
            disabled={isLoading || filteredData.length === 0}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md text-sm font-medium transition duration-200 flex items-center"
            title="Download filtered data as CSV"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download CSV
          </button>
          <button
            onClick={fetchData}
            disabled={isLoading}
            className="bg-gradient-to-r from-orange-400 to-pink-600 hover:from-orange-500 hover:to-pink-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md text-sm font-medium transition duration-200"
          >
            {isLoading ? 'Loading...' : 'Refresh Data'}
          </button>
        </div>
      </div>

      {/* Loading Progress */}
      {isLoading && (
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>Fetching historical data...</span>
            <span>{progress.completed}/{progress.total} periods</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-orange-400 to-pink-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(progress.completed / progress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-64">
          <input
            type="text"
            placeholder="Filter by query..."
            value={queryFilter}
            onChange={(e) => setQueryFilter(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500"
          />
        </div>
        {selectedTrendFilter && (
          <button
            onClick={() => setSelectedTrendFilter(null)}
            className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            Clear Filter
          </button>
        )}
      </div>

      {/* Summary Stats - Clickable Filters */}
      {!isLoading && data.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <button
            onClick={() => handleTrendFilterClick('up')}
            className={`p-3 rounded-lg text-left transition-all duration-200 ${
              selectedTrendFilter === 'up' 
                ? 'bg-green-100 ring-2 ring-green-500 transform scale-105' 
                : 'bg-green-50 hover:bg-green-100 hover:scale-102'
            }`}
          >
            <div className="text-sm font-medium text-green-900">⬆ Improving</div>
            <div className="text-xl font-bold text-green-600">
              {data.filter(item => item.trend === 'up').length}
            </div>
          </button>
          <button
            onClick={() => handleTrendFilterClick('down')}
            className={`p-3 rounded-lg text-left transition-all duration-200 ${
              selectedTrendFilter === 'down' 
                ? 'bg-red-100 ring-2 ring-red-500 transform scale-105' 
                : 'bg-red-50 hover:bg-red-100 hover:scale-102'
            }`}
          >
            <div className="text-sm font-medium text-red-900">⬇ Declining</div>
            <div className="text-xl font-bold text-red-600">
              {data.filter(item => item.trend === 'down').length}
            </div>
          </button>
          <button
            onClick={() => handleTrendFilterClick('stable')}
            className={`p-3 rounded-lg text-left transition-all duration-200 ${
              selectedTrendFilter === 'stable' 
                ? 'bg-gray-100 ring-2 ring-gray-500 transform scale-105' 
                : 'bg-gray-50 hover:bg-gray-100 hover:scale-102'
            }`}
          >
            <div className="text-sm font-medium text-gray-900">⬌ Stable</div>
            <div className="text-xl font-bold text-gray-600">
              {data.filter(item => item.trend === 'stable').length}
            </div>
          </button>
          <button
            onClick={() => handleTrendFilterClick('new')}
            className={`p-3 rounded-lg text-left transition-all duration-200 ${
              selectedTrendFilter === 'new' 
                ? 'bg-blue-100 ring-2 ring-blue-500 transform scale-105' 
                : 'bg-blue-50 hover:bg-blue-100 hover:scale-102'
            }`}
          >
            <div className="text-sm font-medium text-blue-900">🌟 New</div>
            <div className="text-xl font-bold text-blue-600">
              {data.filter(item => item.trend === 'new').length}
            </div>
          </button>
        </div>
      )}


      {/* Historical Rankings Table */}
      {!isLoading && filteredData.length > 0 && (
        <div className="overflow-auto max-h-[48rem]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Query
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trend
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  1mo
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  2mo
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  3mo
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  4mo
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  5mo
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  6mo
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Change
                </th>
                {(onAddTodo || onTrackKeyword) && (
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                    <div className="font-medium">{item.query}</div>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`text-lg ${getTrendColor(item.trend)}`} title={item.trend}>
                      {getTrendIcon(item.trend)}
                    </span>
                  </td>
                  <td className={`px-3 py-3 text-center text-sm font-medium ${getPositionStyle(item.positions.current, item.positions.month1)}`}>
                    {formatPosition(item.positions.current)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-600">
                    {formatPosition(item.positions.month1)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-600">
                    {formatPosition(item.positions.month2)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-600">
                    {formatPosition(item.positions.month3)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-600">
                    {formatPosition(item.positions.month4)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-600">
                    {formatPosition(item.positions.month5)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-600">
                    {formatPosition(item.positions.month6)}
                  </td>
                  <td className={`px-3 py-3 text-center text-sm font-medium ${item.changeFromPrevious && item.changeFromPrevious > 0 ? 'text-green-600' : item.changeFromPrevious && item.changeFromPrevious < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                    {formatPositionChange(item.changeFromPrevious)}
                  </td>
                  {(onAddTodo || onTrackKeyword) && (
                    <td className="px-3 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {onTrackKeyword && (
                          <button
                            onClick={() => isKeywordTracked(item.query) ? handleUntrackKeyword(item) : handleTrackKeyword(item)}
                            disabled={trackingKeywords.has(item.query)}
                            className={`text-xs px-2 py-1 rounded transition-all duration-200 ${
                              isKeywordTracked(item.query)
                                ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700'
                                : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                            title={isKeywordTracked(item.query) ? "Stop tracking this keyword" : "Track this keyword"}
                          >
                            {trackingKeywords.has(item.query) ? '...' : isKeywordTracked(item.query) ? 'Untrack' : 'Track'}
                          </button>
                        )}
                        {onAddTodo && (
                          <button
                            onClick={() => handleAddTodo(item)}
                            disabled={addingTodos.has(item.query)}
                            className="text-xs bg-gradient-to-r from-orange-500 to-pink-500 text-white px-2 py-1 rounded hover:from-orange-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                            title="Add this ranking optimization to your task list"
                          >
                            {addingTodos.has(item.query) ? '...' : '+ Task'}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredData.length === 0 && data.length > 0 && (
        <div className="text-center py-8">
          <div className="text-gray-500">No rankings match your current filters</div>
          <button
            onClick={() => {
              setQueryFilter('');
              setSelectedTrendFilter(null);
            }}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800"
          >
            Clear filters
          </button>
        </div>
      )}

      {!isLoading && data.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-500">No historical ranking data available</div>
          <p className="text-sm text-gray-400 mt-1">
            Try selecting a different property or date range
          </p>
        </div>
      )}

      {/* Upgrade Modal for CSV Export */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Upgrade to Pro Plan</h3>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                CSV exports are available on the Pro plan. Export your data to Excel or Google Sheets for deeper analysis and reporting.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Pro Plan includes:</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    CSV exports for all data
                  </li>
                  <li className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Unlimited GSC properties
                  </li>
                  <li className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    16 months of historical data
                  </li>
                  <li className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    AI-powered content briefs
                  </li>
                </ul>
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-1">£15/month</p>
              <p className="text-sm text-gray-600">Including VAT</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Maybe Later
              </button>
              <a
                href="https://buy.stripe.com/5kA9CV7tF7iw8IEcMP"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-center font-medium"
              >
                Upgrade Now
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoricalRankingsPanel;